import * as THREE from 'three';

/**
 * Reach, drawn on the floor.
 *
 * One ring per strike, at that move's measured reach, parented to the fighter
 * so it rides them around the arena. Spacing is the hardest thing in the game
 * to reason about from a screenshot — two fighters a metre apart and two
 * fighters half a metre apart look nearly identical from a three-quarter view,
 * and the difference between them is whether a punch lands at all.
 *
 * A full circle, not an arc in front. Facing is already visible from the
 * mannequin, and the question this answers is the spacing one: at this
 * distance, is the blow even a possibility?
 *
 * Off by default. This is a debug gizmo, and a game that ships with range
 * circles under the fighters has answered a question the player should be
 * learning to feel.
 */

/** Ring line thickness, in metres. Thin enough to read the floor grid through. */
const THICKNESS = 0.013;

const COLORS = [0x6fd3a0, 0xe2b04a, 0x8f8fe0, 0xd06fa8];

export function createReachRings(moves, { name = 'reach', visible = false } = {}) {
  const object3D = new THREE.Group();
  object3D.name = name;
  object3D.visible = visible;
  // Just clear of the floor plane and the grid helper sitting at y = 0.002.
  object3D.position.y = 0.012;

  const rings = [];

  function clear() {
    for (const ring of rings) {
      ring.geometry.dispose();
      ring.material.dispose();
      ring.mesh.removeFromParent();
    }
    rings.length = 0;
  }

  function build(set) {
    clear();
    // Widest first, so a narrower ring drawn on top of it is the one that wins
    // the depth fight at the overlap.
    const entries = Object.entries(set)
      .filter(([, move]) => move.reach > 0)
      .sort((a, b) => b[1].reach - a[1].reach);
    entries.forEach(ring);
  }

  function ring([move, { reach }], index) {
    const geometry = new THREE.RingGeometry(reach - THICKNESS, reach + THICKNESS, 96);
    geometry.rotateX(-Math.PI / 2);
    const material = new THREE.MeshBasicMaterial({
      color: COLORS[index % COLORS.length],
      transparent: true,
      opacity: 0.72,
      // The floor is a lie the gizmo is drawn on; it must not occlude anything
      // or fight the grid for the same depth.
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = `${name}-${move}`;
    object3D.add(mesh);
    rings.push({ move, reach, mesh, geometry, material });
  }
  build(moves);

  return {
    object3D,
    /** `[{ move, reach }]`, widest first. */
    get list() {
      return rings.map(({ move, reach }) => ({ move, reach }));
    },
    get visible() {
      return object3D.visible;
    },

    /** Redrawn for a body that reaches differently. Same group, same visibility. */
    setMoves(next) {
      build(next);
    },

    setVisible(next) {
      object3D.visible = Boolean(next);
      return object3D.visible;
    },

    dispose() {
      clear();
      object3D.removeFromParent();
    },
  };
}
